// src/pages/AI/Roadmap.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO";

const BASE_URL = process.env.REACT_APP_BASE_URL || "https://myboq.in";
const WEBSITE_NAME = process.env.REACT_APP_WEBSITE_NAME || "myBOQ";

// ---- CLARITY helper ----
const track = (name, props = {}) => {
  if (typeof window !== "undefined" && window.clarity) {
    window.clarity("event", name, props);
  }
};

const Roadmap = ({ openContactModal }) => {
  useEffect(() => {
    track("ai_roadmap_view", { path: "/ai/roadmap" });
  }, []);

  const requestPilot = (placement = "ai_roadmap") => {
    track("ai_pilot_request_click", { placement, intent: "ai_pilot" });
    if (openContactModal) openContactModal();
  };

  return (
    <main className="bg-white">
      <SEO
        title={`${WEBSITE_NAME} | AI Roadmap`}
        description="myBOQ AI roadmap focused on faster estimation, better SSR item discovery, verification-friendly outputs, and governance-ready standardization for public infrastructure workflows."
        keywords="myBOQ AI roadmap, AI estimation, SSR item search, verification friendly outputs, government estimation automation, infrastructure planning"
        image={`${BASE_URL}/ssrlogo3.png`}
        url={`${BASE_URL}/ai/roadmap`}
      />

      {/* HERO */}
      <section className="px-5 sm:px-6 lg:px-20 pt-10 pb-10">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <div className="text-sm text-gray-500">
            <Link
              to="/"
              className="hover:text-orange-600"
              onClick={() => track("nav_click", { item: "home", from: "ai_roadmap_breadcrumb" })}
            >
              Home
            </Link>
            <span className="mx-2">/</span>
            <Link
              to="/ai"
              className="hover:text-orange-600"
              onClick={() => track("nav_click", { item: "ai_overview", from: "ai_roadmap_breadcrumb" })}
            >
              AI &amp; Innovation
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-700 font-semibold">AI Roadmap</span>
          </div>

          <div className="mt-4 grid lg:grid-cols-12 gap-10 items-start">
            {/* Left */}
            <div className="lg:col-span-7">
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight">
                AI Roadmap for Faster, Smarter, Verification-Friendly Estimation
              </h1>

              <p className="mt-4 text-gray-700 text-base sm:text-lg leading-relaxed">
                myBOQ is building AI capabilities focused on real estimation outcomes:{" "}
                <span className="font-semibold text-gray-900">
                  speed, accuracy, standardization, and audit readiness
                </span>
                . Our roadmap prioritizes practical workflows for MH PWD SSR today — and scales to more
                departments and states over time.
              </p>

              <p className="mt-3 text-gray-600">
                This roadmap is designed for engineers, consultants, contractors, and public-sector
                verification teams.
              </p>

              {/* CTAs */}
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => requestPilot("ai_roadmap_hero")}
                  className="inline-flex items-center justify-center rounded-full bg-orange-500 px-6 py-3 text-white font-semibold hover:bg-orange-600 transition"
                >
                  Join AI Pilot
                </button>

                <Link
                  to="/ai/pilot"
                  className="inline-flex items-center justify-center rounded-full bg-gray-100 px-6 py-3 text-gray-900 font-semibold hover:bg-gray-200 transition"
                  onClick={() => track("nav_click", { item: "ai_pilot", placement: "ai_roadmap_hero" })}
                >
                  Pilot Program Details
                </Link>
              </div>

              {/* Chips */}
              <div className="mt-6 flex flex-wrap gap-2">
                {[
                  "SSR item discovery",
                  "Auto validation checks",
                  "Faster report generation",
                  "Governance-ready standardization",
                ].map((chip) => (
                  <span
                    key={chip}
                    className="text-xs sm:text-sm px-3 py-1 rounded-full bg-orange-50 text-orange-800 border border-orange-100"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>

            {/* Right card */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center">
                    <span className="text-xl" aria-hidden="true">
                      🤖
                    </span>
                  </div>

                  <div>
                    <div className="text-gray-900 font-extrabold text-lg">AI goals</div>
                    <p className="text-gray-600 text-sm mt-1">
                      Reduce manual effort and rework while keeping outputs compliant and
                      verification-friendly.
                    </p>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {[
                    "Faster SSR item search",
                    "Suggested templates / items",
                    "Auto checks before export",
                    "Better estimation accuracy",
                  ].map((item) => (
                    <div key={item} className="rounded-xl border border-gray-200 p-3 bg-gray-50">
                      <span className="text-gray-900 font-semibold">✔</span>{" "}
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-5 text-xs text-gray-500">
                  Roadmap is staged and rollout depends on pilot learnings and validation.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROADMAP PHASES */}
      <section className="px-5 sm:px-6 lg:px-20 pb-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">Roadmap Phases</h2>
            <div className="text-sm text-gray-500">Practical AI — built around real estimation workflows</div>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            {/* Phase 1 */}
            <div className="rounded-2xl border border-orange-200 bg-orange-50 p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-xl bg-white/70 border border-orange-100 flex items-center justify-center">
                  <span className="text-xl" aria-hidden="true">
                    ⚡
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900">Phase 1: Speed & Discovery</h3>
                  <p className="mt-1 text-sm text-gray-700">
                    Make estimation faster by helping users find the right SSR items and reduce manual searching.
                  </p>
                </div>
              </div>

              <ul className="mt-4 text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li>Improved SSR item search suggestions</li>
                <li>Related item recommendations</li>
                <li>Common item bundles for typical works</li>
                <li>Better keywords & synonym mapping</li>
              </ul>
            </div>

            {/* Phase 2 */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center">
                  <span className="text-xl" aria-hidden="true">
                    ✅
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900">Phase 2: Validation & Quality</h3>
                  <p className="mt-1 text-sm text-gray-700">
                    Reduce errors by validating before export and highlighting missing inputs.
                  </p>
                </div>
              </div>

              <ul className="mt-4 text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li>Pre-export validation checklist</li>
                <li>Missing measurements / lead inputs detection</li>
                <li>Consistency checks for SSR rules</li>
                <li>Improved recap/abstract outputs</li>
              </ul>
            </div>

            {/* Phase 3 */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center">
                  <span className="text-xl" aria-hidden="true">
                    🧠
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900">Phase 3: Assisted Estimation</h3>
                  <p className="mt-1 text-sm text-gray-700">
                    Suggest structure and items based on the type of work and the estimate pattern.
                  </p>
                </div>
              </div>

              <ul className="mt-4 text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li>Suggested estimate templates</li>
                <li>Auto-fill common item structures</li>
                <li>Assist with lead/consumption patterns</li>
                <li>Improve revision speed</li>
              </ul>
            </div>

            {/* Phase 4 */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center">
                  <span className="text-xl" aria-hidden="true">
                    🏛️
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900">Phase 4: Governance & Scale</h3>
                  <p className="mt-1 text-sm text-gray-700">
                    Standardize across departments and expand beyond Maharashtra.
                  </p>
                </div>
              </div>

              <ul className="mt-4 text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li>Department-specific output policies</li>
                <li>Audit-friendly export packs</li>
                <li>Multi-state SSR mapping strategy</li>
                <li>Institution training integration</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* DARK CTA FOOTER */}
      <section className="px-5 sm:px-6 lg:px-20 pb-14">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl bg-[#0B1B3A] text-white p-7 sm:p-10 shadow-md">
            <h2 className="text-2xl sm:text-3xl font-extrabold">Want early access?</h2>

            <p className="mt-3 text-white/80 leading-relaxed">
              Join the AI Pilot program to shape features based on real estimation workflows. We’ll
              prioritize feedback from active practitioners and institutional partners.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => requestPilot("ai_roadmap_footer")}
                className="inline-flex items-center justify-center rounded-full bg-orange-500 px-6 py-3 text-white font-semibold hover:bg-orange-600 transition"
              >
                Join AI Pilot
              </button>

              <Link
                to="/ai/pilot"
                className="inline-flex items-center justify-center rounded-full bg-white/10 px-6 py-3 text-white font-semibold hover:bg-white/20 transition"
                onClick={() => track("nav_click", { item: "ai_pilot", placement: "ai_roadmap_footer" })}
              >
                View Pilot Program →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Roadmap;